import * as React from 'react';
import { useRouter } from 'next/router';
import { PRICING_PAGE } from 'constants/pages';
import FlowSegmentedControl, { SegmentItem } from 'components/FlowSegmentedControl';
import { expandFlowSideSelection, toggleFlowSideSelection } from 'utils/shared/trades/presets';
import type { FlowSideBasis, FlowSideBasisSelection } from 'utils/shared/trades/presets';

interface Props {
  activeSides: FlowSideBasisSelection;
  onChange: (next: FlowSideBasis[]) => void;
  // Free / anonymous viewers can only see the aggressive ask-side pair (AA, A). Mid, Bid,
  // and Below Bid are premium; when locked they route to the upgrade CTA instead.
  arePremiumSidesLocked?: boolean;
  className?: string;
}

// Compact aggressor codes shown in the top nav, ordered bullish → bearish.
const SIDE_CODES: { value: FlowSideBasis; label: string; premium?: boolean }[] = [
  { value: 'aboveAsk', label: 'AA' },
  { value: 'ask', label: 'A' },
  { value: 'mid', label: 'M', premium: true },
  { value: 'bid', label: 'B', premium: true },
  { value: 'belowBid', label: 'BB', premium: true },
];

const SIDES_TOOLTIP = 'Trade side · AA Above Ask · A Ask · M Mid · B Bid · BB Below Bid';

export default function FlowSidesSelector({
  activeSides,
  onChange,
  arePremiumSidesLocked = false,
  className,
}: Props) {
  const router = useRouter();
  const active = expandFlowSideSelection(activeSides);

  const items: SegmentItem[] = SIDE_CODES.map((side) => ({
    value: side.value,
    label: side.label,
    locked: side.premium && arePremiumSidesLocked,
  }));

  return (
    <FlowSegmentedControl
      ariaLabel="Trade sides"
      tooltip={arePremiumSidesLocked ? `${SIDES_TOOLTIP} · M/B/BB are Pro` : SIDES_TOOLTIP}
      className={className}
      items={items}
      isActive={(value) => active.includes(value as FlowSideBasis)}
      onSelect={(value) => onChange(toggleFlowSideSelection(activeSides, value as FlowSideBasis))}
      onLockedSelect={() => router.push(PRICING_PAGE)}
    />
  );
}
